import { CheckedState } from '@ibsys/kit';
import React, { useCallback, useState } from 'react';
import { Permission, useDeleteRole, useRoles as useGetRoles, usePermissions, useUpdateRole } from '@/entities/roles';

export const useRoles = () => {
  const rolesQuery = useGetRoles({ isDeleted: false });
  const roles = rolesQuery.data?.roles || [];

  const { data: permissionsData } = usePermissions();
  const allPermissions = permissionsData?.permissions || [];

  const { mutateAsync: updateRole } = useUpdateRole();
  const { mutateAsync: deleteRole } = useDeleteRole();

  // Local mocked role->permission map for optimistic UI (keyed by role code)
  const [rolePermissions, setRolePermissions] = useState<Record<string, Set<Permission['code']>>>({});

  // Initialize missing entries when roles change
  React.useEffect(() => {
    if (!roles.length) return;
    setRolePermissions((prev) => {
      const next = { ...prev };
      for (const r of roles) {
        if (!next[r.code]) next[r.code] = new Set<Permission['code']>(r.permissions.map((p) => p.code));
      }
      return next;
    });
  }, [roles]);

  const getTogglePermissions = useCallback(
    (roleCode: string, permission: Permission) => (state: CheckedState) => {
      const oldRole = rolesQuery.data?.roles?.find((r) => r.code === roleCode);

      if (oldRole) {
        // Optimistically update UI mocked state
        setRolePermissions((prev) => {
          const next = { ...prev };
          const set = new Set(next[roleCode] || []);
          if (state) set.add(permission.code);
          else set.delete(permission.code);
          next[roleCode] = set;
          return next;
        });

        try {
          if (state) {
            const nextPermissions = [...(oldRole?.permissions || []), { code: permission.code }];
            updateRole({ code: roleCode, body: { name: oldRole.name, permissions: nextPermissions } });
          } else {
            const nextPermissions = oldRole?.permissions?.filter((r) => r.code !== permission.code);
            updateRole({ code: roleCode, body: { name: oldRole.name, permissions: nextPermissions || [] } });
          }
        } catch (e) {
          // rollback on error
          setRolePermissions((prev) => {
            const next = { ...prev };
            const set = new Set(next[roleCode] || []);
            if (state) set.delete(permission.code);
            else set.add(permission.code);
            next[roleCode] = set;
            return next;
          });
        }
      }
    },
    [rolesQuery, updateRole],
  );

  const isChecked = (roleCode: string, permission: Permission) => {
    return !!rolePermissions[roleCode]?.has(permission.code);
  };

  const isRoleFull = (roleCode: string) => {
    const current = rolePermissions[roleCode];
    return !!current && allPermissions.length > 0 && current.size >= allPermissions.length;
  };

  const toggleFullAccess = async (roleCode: string, grant: boolean) => {
    const oldRole = rolesQuery.data?.roles?.find((r) => r.code === roleCode);
    if (!oldRole) return;

    // optimistic update local set
    setRolePermissions((prev) => {
      const next = { ...prev } as typeof prev;
      next[roleCode] = grant ? new Set(allPermissions.map((p) => p.code)) : new Set();
      return next;
    });

    try {
      const nextPermissions = grant ? allPermissions.map((p) => ({ code: p.code })) : [];
      await updateRole({ code: roleCode, body: { name: oldRole.name, permissions: nextPermissions } });
    } catch (e) {
      // rollback
      setRolePermissions((prev) => {
        const next = { ...prev } as typeof prev;
        next[roleCode] = new Set((oldRole.permissions || []).map((p) => p.code));
        return next;
      });
    }
  };

  const deleteRoleByCode = async (roleCode: string) => {
    try {
      await deleteRole(roleCode);
      setRolePermissions((prev) => {
        const next = { ...prev } as typeof prev;
        delete next[roleCode];
        return next;
      });
    } catch (e) {
      console.error('Failed to delete role', e);
    }
  };

  return {
    getTogglePermissions,
    rolesQuery,
    rolePermissions,
    isChecked,
    isRoleFull,
    toggleFullAccess,
    deleteRoleByCode,
  };
};
